/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import type { CalendarDate } from '@internationalized/date';

import type { DatePickerCloseAction, DatePickerShell } from './DatePicker.types';
import {
    areDatesEqual,
    getSingleDateCloseAction,
    useGetSingleDateTriggerContent,
} from './DatePicker.utils';

interface UseSingleDatePickerDraftOptions {
    value: CalendarDate | null;
    onChange: (value: CalendarDate | null) => void;
}

export interface SingleDatePickerDraft extends DatePickerShell {
    /** The draft date, which only reaches `onChange` when the picker closes. */
    date: CalendarDate | null;
    setDate: React.Dispatch<React.SetStateAction<CalendarDate | null>>;
    closeAction: DatePickerCloseAction;
}

/**
 * Open state and draft date for the single-date picker.
 * The draft resets from the committed value on open and commits on close.
 */
export function useSingleDatePickerDraft({
    value,
    onChange,
}: UseSingleDatePickerDraftOptions): SingleDatePickerDraft {
    const [isOpen, setIsOpen] = React.useState(false);
    const [date, setDate] = React.useState<CalendarDate | null>(value);

    // While closed, show the committed value rather than the stale draft.
    const visibleDate = isOpen ? date : value;
    const triggerContent = useGetSingleDateTriggerContent(visibleDate);
    const hasDates = !!visibleDate;

    const onOpenChange = (open: boolean) => {
        setIsOpen(open);

        // Opening resets the draft from the committed value. That reset is
        // async, so committing here would write the previous draft back out.
        if (open) {
            setDate(value);
            return;
        }

        if (!areDatesEqual(date, value)) {
            onChange(date);
        }
    };

    const onClearDates = React.useCallback(
        ({ commit = true }: { commit?: boolean } = {}) => {
            setDate(null);

            if (commit) {
                onChange(null);
            }
        },
        [onChange]
    );

    return {
        isOpen,
        onOpenChange,
        date,
        setDate,
        onClearDates,
        triggerContent,
        drawerTitleContent: triggerContent,
        hasDates,
        currentTabHasDates: hasDates,
        closeAction: getSingleDateCloseAction(visibleDate, value),
    };
}
